import { usePageRefresh } from '../../../../context/PageRefreshContext';
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { getBRSList, deleteBRS } from '../../../../redux/actions/SocietyPortal/BRSAction/BRSAction';
import { getCashBankList } from '../../../../redux/actions/SocietyPortal/CashBankAction/CashBankAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { financialYears } from '../../../../consts/DropDownOptions';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_ACCOUNTS_RECONCILIATION_DETAIL } from '../../../../Routing/routes';
import BRSCreateModal from './BRSCreateModal';

const STATUS_OPTIONS = [
    { value: 'all', label: 'All Status' },
    { value: 'draft', label: 'Draft' },
    { value: 'finalized', label: 'Finalized' },
];

const STATUS_BADGE = {
    draft: { bg: 'tw-bg-amber-50', text: 'tw-text-amber-600', border: 'tw-border-amber-100' },
    finalized: { bg: 'tw-bg-emerald-50', text: 'tw-text-emerald-600', border: 'tw-border-emerald-100' },
};

const getCurrentFY = () => {
    const now = new Date();
    const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return `${year}-${year + 1}`;
};

const formatCurrency = (amount) =>
    parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDate = (date) => {
    if (!date) return '—';
    const d = new Date(date);
    if (isNaN(d)) return date;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}-${mm}-${d.getFullYear()}`;
};

const Reconciliation = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const societyId = selectedSocietyStorer.getSocietyId();

    const { listData, loading, deleteLoading } = useSelector(state => state.BRSReducer || {});
    const { listData: cashBankList } = useSelector(state => state.CashBankReducer || {});

    const [financialYear, setFinancialYear] = useState(getCurrentFY());
    const [status, setStatus] = useState('all');
    const [accountCd, setAccountCd] = useState('all');
    const [showCreate, setShowCreate] = useState(false);

    const bankAccountOptions = useMemo(() => {
        const options = [{ value: 'all', label: 'All Bank Accounts' }];
        (cashBankList || []).forEach(acc => {
            options.push({
                value: acc.gl_code,
                label: `${acc.name} (${acc.gl_code})`,
            });
        });
        return options;
    }, [cashBankList]);

    const fetchList = () => {
        if (!societyId) return;
        dispatch(getBRSList({
            societyId,
            fin_year: financialYear,
            account_cd: accountCd !== 'all' ? accountCd : undefined,
            status: status !== 'all' ? status : undefined,
            page: 1,
            limit: 50,
        }));
    };
    usePageRefresh(fetchList);

    useEffect(() => {
        if (societyId) {
            fetchList();
            // Ensure bank accounts are available for filter + create modal
            if (!cashBankList || cashBankList.length === 0) {
                dispatch(getCashBankList({ societyId, year: financialYear }));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, societyId, financialYear, status, accountCd]);

    const getAccountName = (code) => {
        const acc = (cashBankList || []).find(a => a.gl_code === code);
        return acc ? acc.name : code;
    };

    const handleView = (id) => {
        navigate(`${SOCIETY_PORTAL}${SOCIETY_PORTAL_ACCOUNTS_RECONCILIATION_DETAIL.replace(':brsId', id)}`);
    };

    const handleDelete = (brs) => {
        if (!window.confirm(`Delete BRS for ${getAccountName(brs.account_cd)} (${formatDate(brs.start_date)} – ${formatDate(brs.end_date)})?`)) return;
        dispatch(deleteBRS({
            societyId,
            brsId: brs.id,
            onSuccess: () => fetchList(),
        }));
    };

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
                {/* Header */}
                <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                    <div className="tw-flex tw-items-center tw-gap-2.5">
                        <div className="tw-w-9 tw-h-9 tw-rounded-lg tw-bg-gradient-to-br tw-from-rose-500 tw-to-pink-600 tw-flex tw-items-center tw-justify-center tw-shadow-md">
                            <Icon icon="hugeicons:structure-03" className="tw-text-white tw-text-base" />
                        </div>
                        <div>
                            <h1 className="tw-text-base tw-font-bold tw-text-gray-800">Bank Reconciliation Statement (BRS)</h1>
                            <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">Reconcile your book entries with bank statements</p>
                        </div>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <button
                            onClick={fetchList}
                            className="tw-p-2 tw-rounded-lg hover:tw-bg-gray-100 tw-text-gray-500"
                            title="Refresh"
                        >
                            <Icon icon="mdi:refresh" className="tw-text-lg" />
                        </button>
                        <button
                            onClick={() => setShowCreate(true)}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-lg tw-text-xs tw-font-medium tw-bg-rose-600 tw-text-white hover:tw-bg-rose-700 tw-transition-colors"
                        >
                            <Icon icon="mdi:plus" className="tw-text-sm" />
                            New BRS
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-bg-gray-50/50">
                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                        <div>
                            <label className="tw-block tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-mb-1">Financial Year</label>
                            <SearchableDropdown
                                value={financialYear}
                                onChange={setFinancialYear}
                                options={financialYears}
                                placeholder="Financial Year"
                                clearable={false}
                                small
                            />
                        </div>
                        <div>
                            <label className="tw-block tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-mb-1">Status</label>
                            <SearchableDropdown
                                value={status}
                                onChange={setStatus}
                                options={STATUS_OPTIONS}
                                placeholder="Status"
                                clearable={false}
                                small
                            />
                        </div>
                        <div>
                            <label className="tw-block tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-mb-1">Bank Account</label>
                            <SearchableDropdown
                                value={accountCd}
                                onChange={setAccountCd}
                                options={bankAccountOptions}
                                placeholder="Bank Account"
                                clearable={false}
                                small
                            />
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-xs">
                        <thead>
                            <tr className="tw-bg-gradient-to-r tw-from-slate-50 tw-to-gray-50">
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Bank Account</th>
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Period</th>
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Financial Year</th>
                                <th className="tw-text-right tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Book Balance</th>
                                <th className="tw-text-right tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Bank Balance</th>
                                <th className="tw-text-center tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Status</th>
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Created</th>
                                <th className="tw-text-center tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="tw-divide-y tw-divide-gray-50">
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="tw-text-center tw-py-12 tw-text-gray-400">
                                        <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                            <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-gray-200 tw-border-t-rose-500"></div>
                                            <span className="tw-text-xs">Loading BRS records...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (listData || []).length > 0 ? (
                                listData.map(brs => {
                                    const badge = STATUS_BADGE[brs.status] || STATUS_BADGE.draft;
                                    return (
                                        <tr key={brs.id} className="hover:tw-bg-slate-50/50 tw-transition-colors">
                                            <td className="tw-px-4 tw-py-3 tw-font-medium tw-text-gray-800">
                                                {getAccountName(brs.account_cd)}
                                                <span className="tw-block tw-text-[10px] tw-text-gray-400 tw-font-mono">{brs.account_cd}</span>
                                            </td>
                                            <td className="tw-px-4 tw-py-3 tw-text-gray-600 tw-whitespace-nowrap">
                                                {formatDate(brs.start_date)} – {formatDate(brs.end_date)}
                                            </td>
                                            <td className="tw-px-4 tw-py-3 tw-text-gray-600">{brs.fin_year}</td>
                                            <td className="tw-px-4 tw-py-3 tw-text-right tw-font-medium tw-text-gray-700">₹{formatCurrency(brs.book_balance)}</td>
                                            <td className="tw-px-4 tw-py-3 tw-text-right tw-font-medium tw-text-gray-700">₹{formatCurrency(brs.bank_balance)}</td>
                                            <td className="tw-px-4 tw-py-3 tw-text-center">
                                                <span className={`tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-medium tw-capitalize tw-border ${badge.bg} ${badge.text} ${badge.border}`}>
                                                    {brs.status}
                                                </span>
                                            </td>
                                            <td className="tw-px-4 tw-py-3 tw-text-gray-500 tw-whitespace-nowrap">{formatDate(brs.created_at)}</td>
                                            <td className="tw-px-4 tw-py-3 tw-text-center">
                                                <div className="tw-flex tw-items-center tw-justify-center tw-gap-1">
                                                    <button
                                                        onClick={() => handleView(brs.id)}
                                                        className="tw-p-1 tw-rounded tw-bg-rose-50 tw-text-rose-600 hover:tw-bg-rose-100 tw-transition-colors"
                                                        title="View / Reconcile"
                                                    >
                                                        <Icon icon="mdi:eye-outline" className="tw-text-sm" />
                                                    </button>
                                                    {brs.status === 'draft' && (
                                                        <button
                                                            onClick={() => handleDelete(brs)}
                                                            disabled={deleteLoading}
                                                            className="tw-p-1 tw-rounded tw-bg-red-50 tw-text-red-500 hover:tw-bg-red-100 tw-transition-colors disabled:tw-opacity-50"
                                                            title="Delete"
                                                        >
                                                            <Icon icon="mdi:trash-can-outline" className="tw-text-sm" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={8} className="tw-text-center tw-py-12">
                                        <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                            <Icon icon="hugeicons:structure-03" className="tw-text-3xl tw-text-gray-300" />
                                            <p className="tw-text-xs tw-text-gray-400">No BRS records found. Click <strong className="tw-text-gray-600">New BRS</strong> to create one.</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {showCreate && (
                <BRSCreateModal
                    financialYear={financialYear}
                    bankAccounts={cashBankList || []}
                    onClose={() => setShowCreate(false)}
                    onSaved={() => { setShowCreate(false); fetchList(); }}
                />
            )}
        </div>
    );
};

export default Reconciliation;
